import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import _ from 'lodash';
import { CloseAddBrickDialogAction } from '../../brick-menu/add-new-brick-dialog.actions';
import { CustomColumnNamesState } from '../../custom-column-names/custom-column-names.state';
import { BrickInstancesSelectors } from '../brick-instances/brick-instances.selectors';
import { LayoutBricksSelectors } from '../general-selectors/layout-bricks.selectors';
import { CloseBrickSettingsDialog, OpenBrickSettingsDialog } from './brick-settings.actions';
import { CommonBrickSettingsState } from './common-brick-settings/common-brick-settings.state';
import { AgentDetailsSettingState } from './specific-brick-settings/agent-details-brick-settings/agent-details-settings.state';
import { AgentListSettingState } from './specific-brick-settings/agent-list-setting/agent-list-setting.state';
import { CallControlSettingsState } from './specific-brick-settings/call-control-settings/call-control-settings.state';
import { CallHistoryBrowserSettingsState } from './specific-brick-settings/call-history-browser-settings/call-history-browser-settings.state';
import { DirectoryDetailsSettingState } from './specific-brick-settings/directory-details-brick-settings/directory-details-settings.state';
import { DirectorySettingsState } from './specific-brick-settings/directory-settings/directory-settings.state';
import { DynamicActionsBrickSettingsState } from './specific-brick-settings/dynamic-actions-brick-settings/dynamic-actions-brick-settings.state';
import { GrafanaDashboardViewerSettingsState } from './specific-brick-settings/grafana-dashboard-viewer-brick-settings/grafana-dashboard-viewer-brick-settings.state';
import { InnerWallBrickSettingsState } from './specific-brick-settings/inner-wall-brick-settings/inner-wall-brick-settings.state';
import { PresenceDurationBrickSettingsState } from './specific-brick-settings/presence-duration-brick-settings/presence-duration-brick-settings.state';
import { SingleCallTagBrickSettingsState } from './specific-brick-settings/single-call-tag-brick-settings/single-call-tag-brick-settings.state';
import { StageSettingsState } from './specific-brick-settings/stage-settings/stage-settings.state';
import { TabBrickSettingsState } from './specific-brick-settings/tab-brick-settings/tab-brick-settings.state';
import { ToolbarBrickSettingsState } from './specific-brick-settings/toolbar-brick-settings/toolbar-brick-settings.state';
import { ToolbarSettingsState } from './specific-brick-settings/toolbar-settings/toolbar-settings.state';
import { UserStatusBrickSettingsState } from './specific-brick-settings/user-status-brick-settings/user-status-brick-settings.state';
import { WebViewerSettingState } from './specific-brick-settings/webviewer/webviewer-settings.state';

// tslint:disable-next-line:no-empty-interface
export interface BrickSettingsStateModel {
   brickInstanceIdForBrickSettingsDialog: string;
}

@State<BrickSettingsStateModel>({
   name: 'brickSettings',
   defaults: {
      brickInstanceIdForBrickSettingsDialog: null,
   },
   children: [
      WebViewerSettingState,
      UserStatusBrickSettingsState,
      ToolbarBrickSettingsState,
      ToolbarSettingsState,
      TabBrickSettingsState,
      StageSettingsState,
      SingleCallTagBrickSettingsState,
      PresenceDurationBrickSettingsState,
      InnerWallBrickSettingsState,
      GrafanaDashboardViewerSettingsState,
      DynamicActionsBrickSettingsState,
      DirectorySettingsState,
      DirectoryDetailsSettingState,
      CustomColumnNamesState,
      CommonBrickSettingsState,
      CallHistoryBrowserSettingsState,
      CallControlSettingsState,
      AgentListSettingState,
      AgentDetailsSettingState,
   ],
})
@Injectable()
export class BrickSettingsState {
   constructor(private store: Store) {}

   public static isBrickSettingDialogOpenForBrickInstance(brickInstanceId: string) {
      return createSelector(
         [BrickSettingsState, BrickInstancesSelectors.isBrickWallEditable],
         (state: BrickSettingsStateModel, isBrickWallEditable: boolean) => {
            return isBrickWallEditable && brickInstanceId === state.brickInstanceIdForBrickSettingsDialog;
         }
      );
   }

   @Selector([BrickSettingsState])
   public static isBrickBeingEditedFn(state): (brickInstanceId: string) => boolean {
      return (brickInstanceId: string) => {
         return state.brickInstanceIdForBrickSettingsDialog === brickInstanceId;
      };
   }

   @Selector([
      BrickSettingsState,
      BrickInstancesSelectors.isBrickWallEditable,
      LayoutBricksSelectors.getBrickInstanceIdOfParentFn,
      LayoutBricksSelectors.getIsBrickInstanceHostingChildBrick,
   ])
   public static isBrickControlPossible(
      state,
      isBrickWallEditable,
      getBrickInstanceIdOfParentFn,
      getIsBrickInstanceHostingChildBrickFn
   ): (brickInstanceId: string) => boolean {
      return (brickInstanceId: string) => {
         if (!isBrickWallEditable) {
            return false;
         }

         const parentBrick = getBrickInstanceIdOfParentFn(brickInstanceId);
         // no overlay for child bricks
         if (!_.isEmpty(parentBrick)) {
            return false;
         }

         // overlay active for top level bricks when they are edited or no brick is edited yet
         if (brickInstanceId === state.brickInstanceIdForBrickSettingsDialog || _.isEmpty(state.brickInstanceIdForBrickSettingsDialog)) {
            return true;
         }

         // overlay active for top level bricks when the edited brick is not a child
         const editedBrickIsChild = getIsBrickInstanceHostingChildBrickFn(brickInstanceId, state.brickInstanceIdForBrickSettingsDialog, true);
         if (!editedBrickIsChild) {
            return true;
         }

         return false;
      };
   }

   @Action(OpenBrickSettingsDialog)
   public openBrickSettingsDialog(ctx: StateContext<BrickSettingsStateModel>, action: OpenBrickSettingsDialog) {
      ctx.patchState({
         brickInstanceIdForBrickSettingsDialog: action.brickInstanceId,
      });
      ctx.dispatch(new CloseAddBrickDialogAction());
   }

   @Action(CloseBrickSettingsDialog)
   public closeBrickSettingsDialog(ctx: StateContext<BrickSettingsStateModel>, action: CloseBrickSettingsDialog) {
      let newBrickInstanceId = null;
      // automatically open settings menu of parent when closing the dialog of a brick hosted inside a tab or inner wall brick
      if (action.openParentSettings) {
         newBrickInstanceId = this.store.selectSnapshot(LayoutBricksSelectors.getBrickInstanceIdOfParentFn)(
            ctx.getState().brickInstanceIdForBrickSettingsDialog
         );
      }

      ctx.patchState({
         brickInstanceIdForBrickSettingsDialog: newBrickInstanceId,
      });
   }
}
